import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { AlertTriangle, ArrowDownLeft, ArrowUpRight, Bell, Building2, Check, Copy, FileText, Inbox, Paperclip, PanelLeftClose, PanelLeftOpen, Printer, Search, Settings2 } from 'lucide-react';
import { Button } from '@/shared/ui/Button';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Input } from '@/shared/ui/Input';
import { ConfirmDialog } from '@/shared/ui/Modal';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Skeleton } from '@/shared/ui/Skeleton';
import { useToast } from '@/shared/ui/Toast';
import { useAuth } from '@/shared/hooks/useAuth';
import { formatDate, formatRelativeTime } from '@/shared/utils/format';
import patterns from '@/shared/ui/styles/patterns.module.css';
import { apiErrorMessage, referralsApi, type RecipientOption, type ReferralDetail, type ReferralListItem } from '../api/referralsApi';
import { useReferral, useReferralList, useReferralMutations, useReferralStats, useReferralTemplate } from '../hooks';
import {
  AttachmentDropzone,
  OUTCOME_LABEL,
  RecipientActions,
  RecipientStatusPill,
  ReferralSlipView,
  ReferralStatusPill,
  ReferralTimeline,
  STATUS_HEADLINE,
  UrgencyPill,
} from '../components';
import styles from './ReferralsPage.module.css';

type Box = 'all' | 'sent' | 'inbox';
type Tab = 'needs_action' | 'all';

/**
 * Operations › Referrals: the list on the left (Needs action / All, sent and received), the slip on
 * the right. Mirrors the receiving-office experience Refera shows, for both directions.
 */
export function ReferralsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('needs_action');
  const [box, setBox] = useState<Box>('all');
  const [q, setQ] = useState('');
  const [listOpen, setListOpen] = useState(true);
  const list = useReferralList({ box, status: 'all', q });
  const stats = useReferralStats();

  const items = useMemo(() => {
    const all = list.data ?? [];
    return tab === 'needs_action' ? all.filter((i) => i.needsAction) : all;
  }, [list.data, tab]);
  const needsActionCount = (list.data ?? []).filter((i) => i.needsAction).length;

  return (
    <div className={clsx(patterns.page, styles.page)}>
      <PageHeader
        title="Referrals"
        subtitle="Send patients to the right office in a few clicks, and see every referral coming in."
        actions={
          <div className={styles.headerActions}>
            <Link to="/referrals/settings" className={clsx(patterns.ctaSecondary, styles.headerLink)}>
              <Settings2 size={14} /> Setup your form
            </Link>
            <Button onClick={() => navigate('/referrals/new')}>Create Referral</Button>
          </div>
        }
      />

      {stats.data && (
        <div className={clsx(patterns.statGrid5, styles.stats)}>
          <Stat label="Sent, 30 days" value={stats.data.sent30d} />
          <Stat label="Received, 30 days" value={stats.data.received30d} />
          <Stat label="Need your action" value={stats.data.awaitingAction} tone={stats.data.awaitingAction ? 'warn' : undefined} />
          <Stat label="Waiting on other offices" value={stats.data.awaitingResponse} />
          <Stat
            label="Scheduled rate"
            value={stats.data.scheduledRate30d == null ? '—' : `${Math.round(stats.data.scheduledRate30d * 100)}%`}
            sub={stats.data.medianHoursToView != null ? `Opened in ~${Math.round(stats.data.medianHoursToView)}h (median)` : undefined}
          />
        </div>
      )}

      <div className={clsx(styles.layout, !listOpen && styles.layoutCollapsed)}>
        <aside className={clsx(styles.list, !listOpen && styles.listHidden)}>
          <Input fullWidth leftIcon={<Search size={14} />} placeholder="Search by patient, doctor or practice name…" value={q} onChange={(e) => setQ(e.target.value)} />
          <div className={styles.tabs} role="tablist">
            <button type="button" role="tab" aria-selected={tab === 'needs_action'} className={clsx(styles.tab, tab === 'needs_action' && styles.tabOn)} onClick={() => setTab('needs_action')}>
              Needs Action <span className={styles.count}>{needsActionCount}</span>
            </button>
            <button type="button" role="tab" aria-selected={tab === 'all'} className={clsx(styles.tab, tab === 'all' && styles.tabOn)} onClick={() => setTab('all')}>
              All <span className={styles.count}>{list.data?.length ?? 0}</span>
            </button>
          </div>
          <div className={patterns.filterGroup}>
            {(['all', 'sent', 'inbox'] as Box[]).map((b) => (
              <button key={b} type="button" className={clsx(patterns.filterPill, box === b && patterns.filterPillActive)} onClick={() => setBox(b)}>
                {b === 'all' ? 'Everything' : b === 'sent' ? 'Sent' : 'Received'}
              </button>
            ))}
          </div>
          <div className={styles.items}>
            {list.isLoading && (
              <>
                <Skeleton height={72} />
                <Skeleton height={72} />
                <Skeleton height={72} />
              </>
            )}
            {!list.isLoading && items.length === 0 && (
              <p className={styles.emptyList}>{tab === 'needs_action' ? 'Nothing needs your action right now.' : 'No referrals yet.'}</p>
            )}
            {items.map((item) => (
              <ListRow key={item.id} item={item} active={item.id === id} onClick={() => navigate(`/referrals/${item.id}`)} />
            ))}
          </div>
        </aside>
        <button type="button" className={styles.collapse} onClick={() => setListOpen((o) => !o)} aria-label={listOpen ? 'Hide list' : 'Show list'}>
          {listOpen ? <PanelLeftClose size={14} /> : <PanelLeftOpen size={14} />}
        </button>
        <main className={styles.detail}>
          {id ? (
            <ReferralDetailPane id={id} />
          ) : (
            <EmptyState
              icon={<Inbox size={28} />}
              title="Select a referral"
              description="Pick one from the list, or create a new referral to send a patient to another office."
              action={{ label: 'Create Referral', onClick: () => navigate('/referrals/new') }}
            />
          )}
        </main>
      </div>
    </div>
  );
}

function Stat({ label, value, sub, tone }: { label: string; value: number | string; sub?: string; tone?: 'warn' }) {
  return (
    <div className={clsx(patterns.tile, styles.stat)}>
      <div className={styles.statLabel}>{label}</div>
      <div className={clsx(styles.statValue, tone === 'warn' && styles.statWarn)}>{value}</div>
      {sub && <div className={styles.statSub}>{sub}</div>}
    </div>
  );
}

function ListRow({ item, active, onClick }: { item: ReferralListItem; active: boolean; onClick: () => void }) {
  const Dir = item.direction === 'sent' ? ArrowUpRight : ArrowDownLeft;
  return (
    <button type="button" className={clsx(styles.row, active && styles.rowActive, item.needsAction && styles.rowNeeds)} onClick={onClick}>
      <div className={styles.rowTop}>
        <span className={styles.rowName}>{item.patientName || 'Unnamed patient'}</span>
        <span className={styles.rowDate}>{item.sentAt ? formatDate(item.sentAt) : 'Draft'}</span>
      </div>
      <div className={styles.rowMid}>
        <span className={styles.rowFrom}>
          <Dir size={12} /> {item.direction === 'sent' ? 'To' : 'From'}: {item.referringDoctorName && item.direction === 'received' ? `${item.referringDoctorName} · ` : ''}
          {item.otherPartyName}
        </span>
        <span className={styles.rowAgo}>{formatRelativeTime(item.updatedAt)}</span>
      </div>
      <div className={styles.rowBottom}>
        <ReferralStatusPill status={item.status} />
        <UrgencyPill urgency={item.aiUrgency && item.aiUrgency !== 'routine' ? item.aiUrgency : item.urgency} />
        {item.isUnconfirmed && (
          <span className={clsx(styles.rowIcon, styles.rowWarn)}>
            <AlertTriangle size={11} /> unconfirmed
          </span>
        )}
        {item.direction === 'sent' && item.recipientCount > 1 && (
          <span className={styles.rowIcon}>
            <Building2 size={11} /> {item.recipientCount} offices
          </span>
        )}
        {item.attachmentCount > 0 && (
          <span className={styles.rowIcon}>
            <Paperclip size={11} /> {item.attachmentCount}
          </span>
        )}
        {item.reportCount > 0 && (
          <span className={styles.rowIcon}>
            <FileText size={11} /> report
          </span>
        )}
      </div>
    </button>
  );
}

function ReferralDetailPane({ id }: { id: string }) {
  const toast = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: r, isLoading, isError, refetch, locationId } = useReferral(id);
  const template = useReferralTemplate(null);
  const { cancel, remind, changeStatus, addNote, addReport, remove, retryPatientText, recordOutcome } = useReferralMutations();
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [searchParams] = useSearchParams();
  const justSentForPrint = searchParams.get('print') === '1';

  if (isLoading) return <Skeleton height={420} />;
  if (isError || !r) return <EmptyState title="Referral not found" description="It may belong to another location, or it was deleted." />;

  const isSender = r.direction === 'sent';
  const multi = r.recipients.length > 1;
  const open = r.status !== 'draft' && r.status !== 'cancelled' && r.status !== 'completed';
  const headline =
    isSender && r.outcome && r.status !== 'draft'
      ? OUTCOME_LABEL[r.outcome]
      : isSender && r.isUnconfirmed
        ? 'No office has confirmed yet'
        : isSender && multi && (r.status === 'sent' || r.status === 'viewed')
          ? `Offered to ${r.recipients.length} offices, waiting on the patient`
          : STATUS_HEADLINE[r.status][isSender ? 'sent' : 'received'];
  const busy = cancel.isPending || remind.isPending || changeStatus.isPending || addNote.isPending || addReport.isPending || recordOutcome.isPending;
  const resolve = (input: { outcome: 'seen' | 'scheduled' | 'not_seen'; recipientId?: string | null }) =>
    recordOutcome
      .mutateAsync({ id: r.id, input })
      .then(() => toast.success(input.outcome === 'not_seen' ? 'Recorded: the patient never went.' : 'Recorded. Thanks for closing the loop.'))
      .catch((e) => toast.error(apiErrorMessage(e, 'Could not record that')));

  const copyLink = async () => {
    if (!r.recipientLinkPreview) return;
    await navigator.clipboard.writeText(r.recipientLinkPreview);
    toast.success('Link copied.');
  };
  const openPrint = (copy: 'office' | 'patient', recipientId?: string) =>
    window.open(
      `/referrals/${r.id}/print?copy=${copy}&locationId=${encodeURIComponent(locationId)}${recipientId ? `&recipient=${encodeURIComponent(recipientId)}` : ''}`,
      '_blank',
      'noopener',
    );
  const deliveredVia = r.deliveryMethods
    .map((m) => (m === 'email' ? 'emailed' : m === 'text' ? 'texted' : m === 'inbox' ? 'in their inbox' : m === 'print' ? 'printed' : m))
    .join(', ');

  return (
    <div className={styles.detailInner}>
      <div className={clsx(styles.banner, ((r.status === 'sent' || r.status === 'viewed') && !isSender) || (isSender && r.isUnconfirmed) ? styles.bannerAction : undefined)}>
        <div>
          <div className={styles.bannerTitle}>{headline}</div>
          <div className={styles.bannerSub}>
            {isSender ? (multi ? r.otherPartyName : `Sent to ${r.recipient?.name ?? '—'}`) : `From ${r.referringDoctorName ? `${r.referringDoctorName}, ` : ''}${r.senderPracticeName}`}
            {isSender && r.outcomeAt && r.outcomeSource ? ` · recorded ${formatDate(r.outcomeAt, 'MMM d')} by ${r.outcomeSource === 'front_desk' ? 'your front desk' : r.outcomeSource === 'report' ? 'their report' : r.outcomeSource === 'patient_text' ? 'the patient' : 'the receiving office'}` : ''}
            {r.sentAt ? ` · ${formatDate(r.sentAt, 'MMM d, yyyy h:mm a')}` : ''}
            {r.scheduledFor ? ` · Appointment ${formatDate(r.scheduledFor, 'EEE, MMM d h:mm a')}` : ''}
            {isSender && deliveredVia ? ` · ${deliveredVia}` : ''}
          </div>
        </div>
        <div className={styles.bannerActions}>
          {isSender && r.status !== 'draft' && r.status !== 'cancelled' && (
            <>
              {!multi && (
                <Button size="sm" variant={justSentForPrint ? 'primary' : 'secondary'} leftIcon={<Printer size={13} />} onClick={() => openPrint('office')}>
                  Print office copy
                </Button>
              )}
              <Button size="sm" variant={multi && justSentForPrint ? 'primary' : 'secondary'} leftIcon={<Printer size={13} />} onClick={() => openPrint('patient')}>
                Print patient copy
              </Button>
            </>
          )}
          {isSender && r.status === 'draft' && (
            <>
              <Button size="sm" onClick={() => navigate(`/referrals/${r.id}/edit`)}>
                Continue editing
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(true)}>
                Delete draft
              </Button>
            </>
          )}
          {isSender && !multi && r.status === 'sent' && (
            <Button size="sm" variant="secondary" leftIcon={<Bell size={13} />} loading={remind.isPending} onClick={() => remind.mutateAsync({ id: r.id }).then(() => toast.success('Reminder sent.')).catch((e) => toast.error(apiErrorMessage(e, 'Could not send a reminder')))}>
              Remind office
            </Button>
          )}
          {isSender && !multi && r.recipientLinkPreview && r.status !== 'draft' && (
            <Button size="sm" variant="ghost" leftIcon={<Copy size={13} />} onClick={() => void copyLink()}>
              Copy link
            </Button>
          )}
          {isSender && (r.status === 'sent' || r.status === 'viewed' || r.status === 'scheduled' || r.status === 'declined') && (
            <Button size="sm" variant="ghost" onClick={() => setConfirmCancel(true)}>
              Cancel referral
            </Button>
          )}
          {isSender && r.status === 'scheduled' && (
            <Button size="sm" variant="secondary" onClick={() => void resolve({ outcome: 'seen' })}>
              Patient was seen
            </Button>
          )}
        </div>
      </div>

      {isSender && r.status !== 'draft' && (
        <OfficesPanel
          r={r}
          open={open}
          busy={busy}
          onRemind={(rid) => remind.mutateAsync({ id: r.id, recipientId: rid }).then(() => toast.success('Reminder sent.')).catch((e) => toast.error(apiErrorMessage(e, 'Could not send a reminder')))}
          onCopy={async (link) => {
            await navigator.clipboard.writeText(link);
            toast.success('Link copied.');
          }}
          onPrint={(rid) => openPrint('office', rid)}
          onResolve={resolve}
        />
      )}

      {!isSender && (
        <RecipientActions
          status={r.status}
          busy={busy}
          defaultActorName={user?.name ?? ''}
          onStatus={(input) => changeStatus.mutateAsync({ id: r.id, input }).catch((e) => toast.error(apiErrorMessage(e, 'Could not update the referral')))}
          onReport={(input) => addReport.mutateAsync({ id: r.id, input }).then(() => toast.success('Report sent to the referring office.')).catch((e) => toast.error(apiErrorMessage(e, 'Could not send the report')))}
          onNote={(note) => addNote.mutateAsync({ id: r.id, note }).catch((e) => toast.error(apiErrorMessage(e, 'Could not add the note')))}
        />
      )}

      {isSender && r.warnings.length > 0 && (
        <div className={styles.warnings}>
          {r.warnings.map((w) => (
            <div key={w}>{w}</div>
          ))}
        </div>
      )}
      {isSender && justSentForPrint && (
        <div className={styles.printNudge}>
          <Printer size={16} />
          <div>
            <strong>Print the slip to hand off.</strong> The office copy carries a QR code to the secure link, so the receiving office can
            open the full referral, files included, with no account. The patient copy says where to go and whom to call.
          </div>
        </div>
      )}
      {isSender && r.patientTextError && r.status !== 'draft' && (
        <div className={styles.warnings}>
          {r.patientTextError && (
            <div className={styles.warnRow}>
              <span>
                The copy for the <strong>patient</strong>
                {r.patientPhone ? ` at ${r.patientPhone}` : ''} could not be texted: {r.patientTextError}. The receiving office
                was not affected.
              </span>
              {r.patientPhone && (
                <Button
                  size="sm"
                  variant="secondary"
                  loading={retryPatientText.isPending}
                  onClick={() =>
                    retryPatientText
                      .mutateAsync(r.id)
                      .then((d) => toast[d.patientTextSentAt ? 'success' : 'error'](d.patientTextSentAt ? 'Patient texted.' : (d.patientTextError ?? 'Still failing.')))
                      .catch((e) => toast.error(apiErrorMessage(e, 'Could not resend')))
                  }
                >
                  Retry patient text
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      <ReferralSlipView
        referral={r}
        teeth={template.data?.teeth}
        bodyRegions={template.data?.bodyRegions}
        numbering={template.data?.settings.toothNumbering}
        viewer={isSender ? 'sender' : 'recipient'}
        attachmentsSlot={
          <AttachmentDropzone
            attachments={r.attachments}
            requestUpload={!isSender && r.status !== 'cancelled' ? (input) => referralsApi.requestUpload(locationId, r.id, input) : undefined}
            confirmUpload={!isSender && r.status !== 'cancelled' ? (aid) => referralsApi.confirmUpload(locationId, r.id, aid) : undefined}
            onDelete={!isSender ? (aid) => referralsApi.deleteAttachment(locationId, r.id, aid).then(() => refetch()) : undefined}
            onChanged={() => void refetch()}
            disabled={isSender}
            disabledReason={r.attachments.length === 0 ? 'No files attached.' : null}
            compact
          />
        }
      />

      <h4 className={styles.timelineTitle}>Activity</h4>
      <ReferralTimeline events={r.events} />

      <ConfirmDialog
        isOpen={confirmCancel}
        onClose={() => setConfirmCancel(false)}
        onConfirm={() => cancel.mutateAsync({ id: r.id }).then(() => setConfirmCancel(false)).catch((e) => toast.error(apiErrorMessage(e, 'Could not cancel')))}
        title="Cancel this referral?"
        message={multi ? 'Every office on the slip is told the referral was withdrawn and their links stop working.' : 'The receiving office is told the referral was withdrawn and their link stops working.'}
        confirmLabel="Cancel referral"
        variant="destructive"
        isLoading={cancel.isPending}
      />
      <ConfirmDialog
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => remove.mutateAsync(r.id).then(() => navigate('/referrals')).catch((e) => toast.error(apiErrorMessage(e, 'Could not delete')))}
        title="Delete this draft?"
        message="The slip and any attached files are removed."
        confirmLabel="Delete draft"
        variant="destructive"
        isLoading={remove.isPending}
      />
    </div>
  );
}

/**
 * The offices this slip was offered to, one row each: how its copy went out, where it stands, and the
 * front desk's one-tap resolution ("Patient was seen here" / "Patient never went") so the loop
 * closes even when no office replies.
 */
function OfficesPanel({
  r,
  open,
  busy,
  onRemind,
  onCopy,
  onPrint,
  onResolve,
}: {
  r: ReferralDetail;
  open: boolean;
  busy: boolean;
  onRemind: (recipientId: string) => Promise<unknown>;
  onCopy: (link: string) => Promise<void>;
  onPrint: (recipientId: string) => void;
  onResolve: (input: { outcome: 'seen' | 'scheduled' | 'not_seen'; recipientId?: string | null }) => Promise<unknown>;
}) {
  const multi = r.recipients.length > 1;
  const unresolved = open && !r.outcome;
  const deliveredVia = (o: RecipientOption) =>
    o.deliveryMethods
      .map((m) => (m === 'email' ? (o.emailSentAt ? `emailed ${o.practice.email ?? ''}`.trim() : o.emailError ? 'email failed' : 'email') : m === 'text' ? (o.textSentAt ? 'texted' : o.textError ? 'text failed' : 'text') : m === 'inbox' ? 'in their PracticeNova inbox' : m === 'print' ? 'printed copy' : m))
      .join(' · ');
  return (
    <section className={styles.offices} aria-label="Offices on this referral">
      {r.isUnconfirmed && !r.outcome && (
        <div className={styles.warnings}>
          <div className={styles.warnRow}>
            <span>
              <strong>No office has confirmed a booking</strong> since this was sent {formatRelativeTime(r.sentAt ?? r.createdAt)}. If the patient told you where
              they went, record it below; otherwise a quick call to the patient may be in order.
            </span>
          </div>
        </div>
      )}
      {r.patientReplyKind && (
        <div className={clsx(styles.warnings, r.patientReplyKind === 'booked' || r.patientReplyKind === 'seen' ? styles.okNote : undefined)}>
          <strong>Patient's text reply{r.patientReplyAt ? ` (${formatRelativeTime(r.patientReplyAt)})` : ''}:</strong>{' '}
          {r.patientReplyKind === 'booked'
            ? 'booked.'
            : r.patientReplyKind === 'seen'
              ? 'already seen.'
              : r.patientReplyKind === 'not_yet'
                ? 'not booked yet. A call from the front desk may help.'
                : r.patientReplyKind === 'wont_go'
                  ? 'not pursuing the referral.'
                  : r.patientReplyKind === 'no_reply'
                    ? 'no answer to the check-in.'
                    : r.patientReplyKind === 'opted_out'
                      ? 'asked not to be texted.'
                      : 'needs a person. Open the thread under Get reviews → Texting → Conversations.'}
        </div>
      )}
      <div className={styles.officesHead}>
        <h4 className={styles.timelineTitle}>{multi ? `Offices (${r.recipients.length})` : 'Receiving office'}</h4>
        {unresolved && (
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => void onResolve({ outcome: 'not_seen' })}>
            Patient never went
          </Button>
        )}
      </div>
      <ul className={styles.officeList}>
        {r.recipients.map((o) => {
          const live = o.status !== 'superseded' && o.status !== 'cancelled';
          return (
            <li key={o.id} className={clsx(styles.officeRow, o.isChosen && styles.officeChosen, !live && styles.officeDim)}>
              <div className={styles.officeMain}>
                <div className={styles.officeName}>
                  {multi && <span className={styles.officePos}>{o.position}</span>}
                  <strong>{o.practice.name}</strong>
                  <RecipientStatusPill status={o.status} />
                  {o.isChosen && multi && (
                    <span className={styles.officeTag}>
                      <Check size={11} /> patient's choice
                    </span>
                  )}
                </div>
                <div className={styles.officeMeta}>
                  {[o.practice.specialtyLabel, o.preferredProviderName ? `Ask for ${o.preferredProviderName}` : null, o.practice.phone].filter(Boolean).join(' · ')}
                </div>
                <div className={styles.officeMeta}>
                  {deliveredVia(o)}
                  {o.viewedAt ? ` · opened ${formatRelativeTime(o.viewedAt)}` : ''}
                  {o.scheduledFor ? ` · appointment ${formatDate(o.scheduledFor, 'EEE, MMM d h:mm a')}` : ''}
                  {o.reminderCount ? ` · ${o.reminderCount} reminder${o.reminderCount > 1 ? 's' : ''}` : ''}
                </div>
                {o.emailError && <div className={styles.officeErr}>The email failed: {o.emailError}</div>}
                {o.textError && <div className={styles.officeErr}>The text failed: {o.textError}</div>}
                {o.declineReason && <div className={styles.officeMeta}>Declined: {o.declineReason}</div>}
              </div>
              <div className={styles.officeActions}>
                {live && o.status === 'sent' && o.practice.email && (
                  <Button size="sm" variant="ghost" leftIcon={<Bell size={12} />} disabled={busy} onClick={() => void onRemind(o.id)}>
                    Remind
                  </Button>
                )}
                {live && o.linkPreview && (
                  <Button size="sm" variant="ghost" leftIcon={<Copy size={12} />} onClick={() => void onCopy(o.linkPreview!)}>
                    Copy link
                  </Button>
                )}
                {live && (
                  <Button size="sm" variant="ghost" leftIcon={<Printer size={12} />} onClick={() => onPrint(o.id)}>
                    Print
                  </Button>
                )}
                {unresolved && live && o.status !== 'completed' && (
                  <Button size="sm" variant="secondary" disabled={busy} onClick={() => void onResolve({ outcome: 'seen', recipientId: o.id })}>
                    Patient was seen here
                  </Button>
                )}
                {unresolved && live && (o.status === 'sent' || o.status === 'viewed' || o.status === 'declined') && (
                  <Button size="sm" variant="ghost" disabled={busy} onClick={() => void onResolve({ outcome: 'scheduled', recipientId: o.id })}>
                    Booked here
                  </Button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
